import type { CaptureResult } from "posthog-js";
import { normalizeRequestPath } from "../normalizePath";

const URL_LIKE_KEY = /url|href|pathname|referrer/i;
const WEB_VITALS_METRIC_EVENT = /^\$web_vitals_.+_event$/;
const DROPPED_KEYS = new Set([
  "$raw_user_agent",
  "$initial_raw_user_agent",
  "$exception_message",
  "$exception_values",
  "$exception_steps",
  "$sentry_exception",
  "$sentry_exception_message",
]);

function scrubUrl(value: string): string {
  if (value.startsWith("$")) {
    return value;
  }
  try {
    const url = new URL(value);
    return `${url.origin}${normalizeRequestPath(url.pathname)}`;
  } catch {
    return normalizeRequestPath(value);
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function scrubValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }
  return isPlainObject(value) ? scrubRecord(value) : value;
}

function withoutMessage(exception: unknown): unknown {
  if (!isPlainObject(exception)) {
    return exception;
  }
  return scrubRecord(Object.fromEntries(Object.entries(exception).filter(([key]) => key !== "value")));
}

function scrubRecord(record: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (DROPPED_KEYS.has(key) || WEB_VITALS_METRIC_EVENT.test(key)) {
      continue;
    }
    if (key === "$exception_list" && Array.isArray(value)) {
      scrubbed[key] = value.map(withoutMessage);
      continue;
    }
    scrubbed[key] = typeof value === "string" && URL_LIKE_KEY.test(key) ? scrubUrl(value) : scrubValue(value);
  }
  return scrubbed;
}

export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event;
  }
  return {
    ...event,
    properties: scrubRecord(event.properties),
    $set: event.$set && scrubRecord(event.$set),
    $set_once: event.$set_once && scrubRecord(event.$set_once),
  };
}
